/**
 * Where to send the user after they sign in.
 *
 * Azure AD B2C always returns to `/index.html` (the registered redirect URI), which the router sends
 * to Home. To land the user where they were going — a transaction linked from an email, or the page
 * they were on when their session expired — the path rides through sign-in in the OAuth `state`
 * (`initLoginFlow(path)`), and is restored from `OAuthService.state` once the user is back.
 */

/** Paths not worth returning to: the app root, the B2C redirect page, and signing out. */
const NO_RETURN_PATHS = new Set(['/', '/index.html', '/logout']);

/**
 * The app path (with query and fragment) to return to after signing in, or `''` when there is none
 * worth keeping.
 */
export function loginReturnPath(
  location: Pick<Location, 'pathname' | 'search' | 'hash'>,
): string {
  return NO_RETURN_PATHS.has(location.pathname)
    ? ''
    : `${location.pathname}${location.search}${location.hash}`;
}

/**
 * A path in this app: one leading `/`, not `//host` or `/\host` (which leave the app), and no control
 * characters (the URL parser drops tabs and newlines, so `/<tab>/host` would become `//host`).
 */
// eslint-disable-next-line no-control-regex
const APP_PATH = /^\/(?![/\\])[^\u0000-\u001F\u007F]*$/;

/**
 * The path carried through sign-in, read back from `OAuthService.state` (which is still URI-encoded),
 * or `''` when there is none or it is not a path in this app.
 */
export function returnPathFromLoginState(
  state: string | null | undefined,
): string {
  if (!state) {
    return '';
  }
  let path: string;
  try {
    path = decodeURIComponent(state);
  } catch {
    return '';
  }
  return APP_PATH.test(path) && !NO_RETURN_PATHS.has(path.split(/[?#]/)[0])
    ? path
    : '';
}
